/**
 * complete-the-look.js
 *
 * DOM glue for the Complete the Look section's Quick_Add_Modal.
 * ES module (loaded via <script type="module">, see sections/complete-the-look.liquid).
 * Vanilla ES6+, module-scoped only — no globals are created, matching the
 * "vanilla JS, no jQuery, ES6+, wrap in IIFE or use modules" coding standard.
 *
 * Mirrors the manual-modal conventions used by `MobilePopup`/`SizeGuide` in
 * product-page-v2.js (focus trap, Escape-to-close, backdrop-click-to-close,
 * close-button-to-close, focus-restoration-on-close), scoped under `ctl`
 * naming instead of `ppv2`, since this section does not use the theme's
 * native <dialog>-based quick-add components (see design.md).
 *
 * Task breakdown across this file (see tasks.md):
 *   11.1 — modal creation + open/close mechanics, focus trap, Escape,
 *          backdrop click, close button, focus restoration.
 *   11.2 — populate-on-open from #ctl-data, close-other-instance behavior,
 *          move focus to first focusable element on open.
 *   11.4 — size option button rendering (THIS task).
 *   11.6 — accessible name wiring (aria-labelledby/aria-label refinement).
 *   13.x — size selection handling + add-to-cart submission.
 */

import { resolveVariant, requiresSizeSelection, buildCardViewModel } from './complete-the-look-logic.js';

const MODAL_ID = 'ctl-quick-add-modal';

// Elements considered part of the modal's tab sequence for the focus trap.
// Re-queried each time the trap runs, since size buttons (task 11.4) are
// added to the DOM dynamically after the modal is created.
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Parses the hidden `#ctl-data` JSON payload emitted by
 * sections/complete-the-look.liquid (task 8.4) — a JSON OBJECT keyed by the
 * same string index used for each quick add control's
 * `data-product-index` attribute (with gaps where products were filtered
 * out), each value shaped as:
 *   { id, title, url, price, compare_at_price, size_option_position, variants }
 *
 * Parsed exactly once at module load time and cached, since the payload is
 * static server-rendered data that never changes after page load — re-
 * parsing it on every quick add click would be wasted work.
 *
 * Defensive: returns an empty object (never throws) if the element is
 * missing or its content is not valid JSON, matching the `if (!dataEl)
 * return;`-style guards already used in product-page-v2.js and the
 * defensive handling called out in design.md's Error Handling table.
 *
 * @returns {Object<string, Object>}
 */
function parseCtlData() {
  const dataEl = document.getElementById('ctl-data');
  if (!dataEl) return {};

  try {
    const parsed = JSON.parse(dataEl.textContent);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    console.warn('CTL: product data not found or invalid');
    return {};
  }
}

const CTL_DATA = parseCtlData();

/**
 * Looks up a single Look_Product's data by its `data-product-index` value.
 * Returns `null` (rather than throwing) when there is no matching entry —
 * this should not occur given the payload is generated for exactly the
 * rendered cards, but is guarded defensively per design.md.
 *
 * @param {string|undefined} productIndex
 * @returns {Object|null}
 */
function getProductDataByIndex(productIndex) {
  if (productIndex === undefined || productIndex === null) return null;
  const entry = CTL_DATA[productIndex];
  return entry || null;
}

/**
 * Formats a price (integer cents, Shopify's standard product JSON
 * convention) into a display string. Mirrors the `formatPrice` helper in
 * product-page-v2.js (same `cents / 100` + `toFixed(2)` + comma-insertion
 * convention) for consistency across the theme's bespoke product surfaces.
 * `#ctl-data` does not carry `shop.money_format` (unlike
 * `product-page-v2.js`'s `ppv2-product-data` payload), so this always
 * falls back to the same default format product-page-v2.js itself falls
 * back to when `money_format` is absent.
 *
 * @param {number} price - integer cents
 * @returns {string}
 */
function formatPrice(price) {
  const moneyFormat = '₹ {{amount}}';
  const cents = price;
  const dollars = (cents / 100).toFixed(2);
  const dollarsWithCommas = dollars.replace(/(\d)(?=(\d{3})+\.)/g, '$1,');

  return moneyFormat.replace('{{amount}}', dollarsWithCommas);
}

/**
 * Builds the shared Quick_Add_Modal markup and appends it to document.body.
 * Reuses an existing element with the same id instead of creating a
 * duplicate, in case this module is evaluated more than once.
 *
 * Structure (see design.md "JavaScript: assets/complete-the-look.js"):
 *   #ctl-quick-add-modal (outer container)
 *     .ctl__modal-backdrop        — click closes the modal
 *     .ctl__modal-panel           — role="dialog", holds all modal content
 *       .ctl__modal-close         — click closes the modal
 *       .ctl__modal-title         — populated in task 11.2
 *       .ctl__modal-price         — populated in task 11.2
 *       .ctl__modal-sizes         — populated in task 11.4
 *       .ctl__modal-status        — populated in task 13.2 (role="status", aria-live="polite")
 *       .ctl__modal-add           — wired in tasks 13.x
 *
 * @returns {{
 *   modal: HTMLElement,
 *   backdrop: HTMLElement,
 *   panel: HTMLElement,
 *   closeBtn: HTMLElement,
 *   title: HTMLElement,
 *   price: HTMLElement,
 *   sizes: HTMLElement,
 *   addButton: HTMLElement,
 *   status: HTMLElement
 * }}
 */
function createModal() {
  const existing = document.getElementById(MODAL_ID);
  if (existing) {
    return {
      modal: existing,
      backdrop: existing.querySelector('[data-ctl-modal-backdrop]'),
      panel: existing.querySelector('[data-ctl-modal-panel]'),
      closeBtn: existing.querySelector('[data-ctl-modal-close]'),
      title: existing.querySelector('[data-ctl-modal-title]'),
      price: existing.querySelector('[data-ctl-modal-price]'),
      sizes: existing.querySelector('[data-ctl-modal-sizes]'),
      addButton: existing.querySelector('[data-ctl-modal-add]'),
      status: existing.querySelector('[data-ctl-modal-status]'),
    };
  }

  const modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.className = 'ctl__modal';
  modal.hidden = true;

  modal.innerHTML = `
    <div class="ctl__modal-backdrop" data-ctl-modal-backdrop></div>
    <div
      class="ctl__modal-panel"
      data-ctl-modal-panel
      role="dialog"
      aria-modal="true"
      aria-labelledby="ctl-modal-title"
      tabindex="-1"
    >
      <button type="button" class="ctl__modal-close" data-ctl-modal-close aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true">
          <path d="M18 6 6 18"></path>
          <path d="m6 6 12 12"></path>
        </svg>
      </button>
      <div class="ctl__modal-content">
        <h2 id="ctl-modal-title" class="ctl__modal-title" data-ctl-modal-title></h2>
        <p class="ctl__modal-price" data-ctl-modal-price></p>
        <div class="ctl__modal-sizes" data-ctl-modal-sizes></div>
        <p class="ctl__modal-status" data-ctl-modal-status role="status" aria-live="polite"></p>
        <button type="button" class="ctl__modal-add" data-ctl-modal-add>Add to Bag</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  return {
    modal,
    backdrop: modal.querySelector('[data-ctl-modal-backdrop]'),
    panel: modal.querySelector('[data-ctl-modal-panel]'),
    closeBtn: modal.querySelector('[data-ctl-modal-close]'),
    title: modal.querySelector('[data-ctl-modal-title]'),
    price: modal.querySelector('[data-ctl-modal-price]'),
    sizes: modal.querySelector('[data-ctl-modal-sizes]'),
    addButton: modal.querySelector('[data-ctl-modal-add]'),
    status: modal.querySelector('[data-ctl-modal-status]'),
  };
}

/**
 * QuickAddModal — manages the single shared #ctl-quick-add-modal instance.
 *
 * `refs` exposes the modal's internal elements so later tasks (11.2 title/
 * price population, 11.4 size buttons, 11.6 accessible name, 13.x add-to-
 * cart wiring) can reach into the modal without re-querying the DOM or
 * duplicating the creation logic above.
 */
const QuickAddModal = (() => {
  const refs = createModal();

  /** @type {HTMLElement|null} The quick add button that opened the modal, for focus restoration. */
  let triggerButton = null;

  /** @type {boolean} */
  let isOpen = false;

  /**
   * The currently populated Look_Product's data, as read from #ctl-data.
   * Stored here (task 11.2) so later tasks — size button rendering (11.4)
   * and add-to-cart submission (13.x) — can read `variants` /
   * `size_option_position` without re-parsing #ctl-data or re-threading the
   * value through every call site.
   * @type {Object|null}
   */
  let currentProductData = null;

  /**
   * Stores the Look_Product data the modal is (about to be) populated
   * with. Exposed so the populate-on-open flow (below) can hand off the
   * resolved product data without reaching into this closure directly.
   * @param {Object|null} productData
   */
  const setProductData = (productData) => {
    currentProductData = productData;
  };

  const getProductData = () => currentProductData;

  /**
   * The currently resolved variant for the populated Look_Product, given
   * the customer's current size selection (or the single-variant/no-size-
   * option special case, task 13.1). Stored here so add-to-cart submission
   * (task 13.2) can read it without recomputing `resolveVariant` itself.
   * @type {Object|null}
   */
  let resolvedVariant = null;

  /**
   * @param {Object|null} variant
   */
  const setResolvedVariant = (variant) => {
    resolvedVariant = variant;
  };

  const getResolvedVariant = () => resolvedVariant;

  /**
   * The size value the customer has currently clicked, or `null` before any
   * click / after (re)populating the modal with a new product.
   * @type {string|null}
   */
  let selectedSize = null;

  /**
   * @param {string|null} size
   */
  const setSelectedSize = (size) => {
    selectedSize = size;
  };

  const getSelectedSize = () => selectedSize;

  /**
   * Opens the modal. Records the triggering button so `close()` can
   * restore focus to it (Requirements 7.9, 8.5), then moves keyboard focus
   * to the modal's first focusable element (Requirement 8.3) — the same
   * `FOCUSABLE_SELECTOR` used by the focus trap above.
   *
   * @param {HTMLElement} [trigger] - the button that activated quick add
   */
  const open = (trigger) => {
    triggerButton = trigger || null;
    isOpen = true;
    refs.modal.hidden = false;
    refs.modal.classList.add('ctl__modal--open');
    document.body.classList.add('ctl-body-locked');

    const focusable = refs.panel.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusable.length > 0) {
      focusable[0].focus();
    }
  };

  /**
   * Closes the modal via any path (close button, backdrop click, Escape)
   * and restores keyboard focus to the triggering quick add control
   * (Requirements 7.9, 8.5).
   */
  const close = () => {
    isOpen = false;
    refs.modal.hidden = true;
    refs.modal.classList.remove('ctl__modal--open');
    document.body.classList.remove('ctl-body-locked');

    if (triggerButton && document.body.contains(triggerButton)) {
      triggerButton.focus();
    }
    triggerButton = null;
    currentProductData = null;
    resolvedVariant = null;
    selectedSize = null;
  };

  // ─── Close triggers ────────────────────────────────────────────────────
  refs.backdrop.addEventListener('click', close);
  refs.closeBtn.addEventListener('click', close);

  // ─── Escape key closes the modal ───────────────────────────────────────
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen) {
      close();
    }
  });

  /**
   * Re-evaluates variant resolution against the given size selection and
   * updates the stored resolved variant plus the add-to-cart button's
   * enabled state accordingly (task 13.1).
   *
   * `refs.addButton` is disabled whenever no variant could be resolved —
   * this covers both "size selection required but not yet made"
   * (Requirement 7.3) and is a no-op (always enabled) for the single-
   * variant/no-size-option case, since `resolveVariant`'s own special case
   * returns that sole variant directly regardless of `selectedSize`
   * (Requirement 7.6).
   *
   * @param {string|null} size - the size to resolve against
   */
  const updateVariantResolution = (size) => {
    const productData = getProductData();
    if (!productData) return;

    const variant = resolveVariant(productData.variants, productData.sizeOptionPosition, size);
    setResolvedVariant(variant);
    refs.addButton.disabled = !variant;
  };

  // ─── Size selection (task 13.1) ────────────────────────────────────────
  // Delegated, since size buttons are dynamically added/removed each time
  // the modal is populated with a new product (renderSizeOptions/
  // populateModal). requiresSizeSelection isn't consulted directly here —
  // resolveVariant's own resolution result is what governs the add button,
  // per Requirements 7.3/7.6.
  refs.sizes.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ctl-size-option]');
    if (!button || button.disabled) return;

    const size = button.dataset.size;
    setSelectedSize(size);

    refs.sizes.querySelectorAll('[data-ctl-size-option]').forEach((sizeButton) => {
      sizeButton.setAttribute('aria-pressed', sizeButton === button ? 'true' : 'false');
    });

    updateVariantResolution(size);
  });

  /**
   * Displays a transient status message in the modal's `role="status"`
   * region (Requirements 7.5, 7.7, 7.8, 7.10). The element is
   * `aria-live="polite"` so screen readers announce the change without
   * stealing focus from the add-to-cart button. No auto-dismiss timer —
   * the message is simply overwritten by the next status update, or
   * cleared implicitly when the modal is repopulated for a new product
   * (Requirement 7.2's "always overwrites all previously displayed
   * content" — see `populateModal`).
   *
   * @param {string} message
   */
  const showStatus = (message) => {
    refs.status.textContent = message;
  };

  /**
   * Updates the header cart count bubble after a successful add-to-cart,
   * mirroring `animateCartBadge()` in product-page-v2.js so the header
   * stays in sync regardless of which add-to-cart surface was used.
   * Without this, `#header-cart-count` — server-rendered once from
   * `cart.item_count` at page load — never reflects items added via this
   * modal until the customer reloads the page.
   *
   * @param {number} quantityAdded - number of units just added (always 1
   *   for this modal, since it only ever POSTs `{ id, quantity: 1 }`).
   */
  const updateCartBadge = (quantityAdded) => {
    const badge = document.getElementById('header-cart-count');
    if (!badge) return;

    const currentCount = parseInt(badge.textContent || '0', 10) || 0;
    const newCount = currentCount + quantityAdded;

    badge.textContent = String(newCount);
    badge.style.display = newCount > 0 ? 'flex' : 'none';

    // Brief scale-up animation, matching product-page-v2.js's own badge
    // feedback for a consistent add-to-cart experience across the theme.
    badge.style.transition = 'transform 0.2s ease';
    badge.style.transform = 'scale(1.4)';
    setTimeout(() => {
      badge.style.transform = 'scale(1)';
    }, 200);

    document.dispatchEvent(
      new CustomEvent('ctl:cart-updated', { detail: { itemCount: newCount } })
    );
  };

  // ─── Add to cart submission (task 13.2) ────────────────────────────────
  // Blocks submission with the appropriate indication when no variant can
  // be resolved (Requirements 7.7, 7.8), otherwise POSTs the resolved
  // variant to Shopify's cart endpoint (Requirement 7.5). On success the
  // modal closes and a confirmation indication is shown; on failure
  // (network error or non-2xx response, e.g. Shopify's 422 for an
  // out-of-stock variant) the modal stays open, the current selection is
  // retained (selectedSize/resolvedVariant are untouched), and a failure
  // indication is shown (Requirement 7.10).
  refs.addButton.addEventListener('click', async () => {
    const productData = getProductData();
    if (!productData) return;

    const requiresSize = requiresSizeSelection(productData.variants, productData.sizeOptionPosition);
    const variant = getResolvedVariant();

    if (!variant) {
      showStatus(requiresSize ? 'Please select a size' : 'This option is currently unavailable');
      return;
    }

    refs.addButton.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: 1 }),
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      showStatus('Added to your bag');
      updateCartBadge(1);
      close();
    } catch (error) {
      showStatus("Couldn't add to bag. Please try again.");
      refs.addButton.disabled = false;
    }
  });

  // ─── Focus trap: Tab/Shift+Tab cycle within the modal panel ────────────
  // Focusable elements are re-queried on every keydown (rather than cached)
  // because size buttons (task 11.4) are inserted into .ctl__modal-sizes
  // after the modal is created.
  refs.panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !isOpen) return;

    const focusable = refs.panel.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  return {
    open,
    close,
    refs,
    isOpen: () => isOpen,
    setProductData,
    getProductData,
    setResolvedVariant,
    getResolvedVariant,
    setSelectedSize,
    getSelectedSize,
    updateVariantResolution,
  };
})();

/**
 * Renders one size option `<button>` per distinct size value found in
 * `variants`, into `container`, and disables any size for which no
 * available variant exists (Requirement 7.4).
 *
 * When `sizeOptionPosition` is null/undefined, the Look_Product has no
 * size-like option (single-variant/no-size-option case, Requirement 7.6 —
 * handled separately via `requiresSizeSelection`), so nothing is rendered.
 *
 * Distinct size values are derived from `variant[\`option${sizeOptionPosition}\`]`,
 * preserving first-seen order and de-duplicated, so the buttons appear in
 * the same order sizes first occur across the variant list.
 *
 * A size value is disabled when there is no variant with that size value
 * AND `available === true`. Native `disabled` on a `<button>` already
 * prevents it from being focused, clicked, or otherwise selected, which
 * satisfies "disabled buttons non-selectable" without any extra handling.
 *
 * `aria-pressed="false"` is set as a baseline state so task 13.1's click
 * handling can toggle it to reflect the customer's current selection
 * without needing to add the attribute for the first time then.
 *
 * Does not clear `container` itself — the caller (`populateModal`) already
 * clears `refs.sizes` before calling this, so clearing again here would
 * just be redundant, duplicate work.
 *
 * @param {HTMLElement} container - the element to append size buttons into (e.g. refs.sizes)
 * @param {Array<Object>} variants - each with optionN fields and `available`
 * @param {number|null|undefined} sizeOptionPosition - 1, 2, or 3; null if no size option
 */
function renderSizeOptions(container, variants, sizeOptionPosition) {
  if (sizeOptionPosition == null) return;

  const optionKey = `option${sizeOptionPosition}`;
  const sizeValues = [];

  variants.forEach((variant) => {
    const size = variant[optionKey];
    if (size !== undefined && size !== null && !sizeValues.includes(size)) {
      sizeValues.push(size);
    }
  });

  sizeValues.forEach((size) => {
    const isAvailable = variants.some(
      (variant) => variant[optionKey] === size && variant.available === true
    );

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ctl__size-option';
    button.setAttribute('data-ctl-size-option', '');
    button.setAttribute('data-size', size);
    button.setAttribute('aria-pressed', 'false');
    button.textContent = size;
    if (!isAvailable) {
      button.disabled = true;
    }

    container.appendChild(button);
  });
}

/**
 * Populates the shared modal's title/price/size-state with a single Look_
 * Product's data (Requirement 7.2). Always overwrites all previously
 * displayed content — there is no per-product modal instance to leak stale
 * content between, since `QuickAddModal` manages exactly one shared
 * element, but repopulating unconditionally here still guarantees no field
 * is left showing a prior product's value.
 *
 * @param {Object} productData - a single entry from #ctl-data
 */
function populateModal(productData) {
  const { refs } = QuickAddModal;

  refs.title.textContent = productData.title;

  // Current price always shown; compare-at price shown only when strictly
  // greater than the current price — mirrors `buildCardViewModel`'s
  // `showCompareAtPrice` decision (Property 4) for consistency with the
  // Product_Card rendering, applied here to the modal instead.
  const { showCompareAtPrice } = buildCardViewModel({
    title: productData.title,
    currentPrice: productData.price,
    compareAtPrice: productData.compare_at_price,
    image: undefined,
    url: productData.url,
  });

  const currentPriceHtml = `<span class="ctl__modal-price-current">${formatPrice(productData.price)}</span>`;
  const comparePriceHtml = showCompareAtPrice
    ? ` <span class="ctl__modal-price-compare">${formatPrice(productData.compare_at_price)}</span>`
    : '';
  refs.price.innerHTML = currentPriceHtml + comparePriceHtml;

  // Clear any size buttons left over from a previously populated product,
  // then render fresh ones for this product (task 11.4). Click handling
  // for size selection is task 13.1's responsibility, not this one's.
  refs.sizes.innerHTML = '';
  renderSizeOptions(refs.sizes, productData.variants, productData.size_option_position);

  // Reset selection state for the newly populated product (task 13.1) —
  // any size selected for a previously populated product must not carry
  // over.
  QuickAddModal.setSelectedSize(null);

  QuickAddModal.setProductData({
    variants: productData.variants,
    sizeOptionPosition: productData.size_option_position,
  });

  // Initialize the add-to-cart button's enabled state immediately, since
  // the single-variant/no-size-option case (Requirement 7.6) never
  // receives a size button click to trigger this otherwise —
  // renderSizeOptions no-ops when size_option_position is null. This also
  // correctly leaves the button disabled for any multi-variant case until
  // a size is clicked (Requirement 7.3).
  QuickAddModal.updateVariantResolution(null);
}

// ─── Quick add activation (delegated) ─────────────────────────────────────
// Reads the activated card's product data from #ctl-data (by
// data-product-index), populates the modal, and opens it (Requirement
// 7.2). Since QuickAddModal manages a single shared modal element rather
// than one modal per card, "close any other open modal instance for a
// different Look_Product first" is satisfied by definition — there is only
// ever one modal. `close()` is still called first (when already open) as
// an explicit guard against any transitional visual glitch from
// repopulating a visible modal in place, matching the requirement's
// wording of "close ... first".
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-ctl-quick-add]');
  if (!trigger) return;

  const productData = getProductDataByIndex(trigger.dataset.productIndex);
  // Defensive: should not occur given #ctl-data is generated for exactly
  // the rendered cards, but no-op rather than throw if the entry is
  // missing (see design.md Error Handling table).
  if (!productData) return;

  if (QuickAddModal.isOpen()) {
    QuickAddModal.close();
  }

  populateModal(productData);
  QuickAddModal.open(trigger);
});

export { QuickAddModal };
